import { styled, Typography } from '@chronosphereio/chrono-ui';
import { PANEL_TOOLTIP_MIN_WIDTH } from './model';

// Constant use to manage horizontal spacing within children (instead of the
// parent), so that scrollbar placement looks nice when content overflows.
const TOOLTIP_HORIZONTAL_SPACING = 2;

/**
 * Wraps and lays out the various content within the tooltip.
 */
export const TooltipLayout = styled('div')(({ theme }) => {
  return {
    flex: 1,
    display: 'flex',
    gap: theme.spacing(0.5),
    flexDirection: 'column',
    // Horizontal padding is managed in individual children to allow for
    // better scrollbar placement for the child with overFlowY: scroll.
    padding: theme.spacing(1, 0),
    maxWidth: '100%',
  };
});

/**
 * Wraps content with a static size that does not grow/shrink in the flex container
 * and does not need to be scrollable for overlowed content.
 */
export const TooltipStaticContentWrapper = styled('div')(({ theme }) => {
  return {
    padding: theme.spacing(0, TOOLTIP_HORIZONTAL_SPACING),
  };
});

/**
 * Wraps content with a dynamic size that grows/shrinks in the flex container
 * and is scrollable when content overloads.
 */
export const TooltipDynamicContentWrapper = styled('div')(({ theme }) => {
  return {
    flex: 1,
    height: 0,
    overflowY: 'auto',
    padding: theme.spacing(0, TOOLTIP_HORIZONTAL_SPACING),
  };
});

/**
 * Layout for individual series row with consistent spacing.
 */
export const TooltipSeriesRow = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  };
});

/**
 * Consistent styling for series names with ellipsis / overflow handling.
 */
export const TooltipSeriesLabel = styled(Typography)(({ theme }) => {
  return {
    fontSize: theme.typography.body2.fontSize,
    lineHeight: theme.typography.body2.lineHeight,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    paddingRight: theme.spacing(2),
    flex: 1, // Ensures series value stays right aligned in tooltip content
  };
});

/**
 * Tooltip overlay which moves with the user's cursor.
 */
export const PanelTooltipOverlay = styled('div')(({ theme }) => ({
  minWidth: PANEL_TOOLTIP_MIN_WIDTH,
  position: 'absolute',
  top: 0,
  left: 0,
  backgroundColor: theme.palette.background.popover,
  borderRadius: '4px',
  boxShadow: theme.shadows[5],
  transition: 'transform 0.1s ease-out',
  zIndex: theme.zIndex.tooltip,
}));
